import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { AlertTriangle, Shield, Lock, Timer } from "lucide-react";
import CTAButton from "@/components/funnel/CTAButton";
import StripeBuyButton from "@/components/funnel/StripeBuyButton";
import { LANDING } from "@/constants/testIds";

const HERO_BG =
  "https://images.unsplash.com/photo-1672552226380-486fe900b322?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHwyfHxjYXJkYm9hcmQlMjBib3hlcyUyMHdhcmVob3VzZSUyMGRhcmt8ZW58MHx8fHwxNzgzMjk1MDIzfDA&ixlib=rb-4.1.0&q=85";

function useCountdown(seconds = 15 * 60) {
  const [time, setTime] = useState(seconds);
  useEffect(() => {
    const id = setInterval(() => {
      setTime((t) => (t <= 1 ? seconds : t - 1));
    }, 1000);
    return () => clearInterval(id);
  }, [seconds]);
  const h = Math.floor(time / 3600);
  const m = Math.floor((time % 3600) / 60);
  const s = time % 60;
  return {
    hh: String(h).padStart(2, "0"),
    mm: String(m).padStart(2, "0"),
    ss: String(s).padStart(2, "0"),
  };
}

export function UrgencyBar() {
  const { mm, ss } = useCountdown(15 * 60);
  return (
    <div
      data-testid={LANDING.urgencyBar}
      className="w-full bg-[#F04632] text-white text-xs sm:text-sm font-bold py-2.5 px-4 text-center sticky top-0 z-50 flex items-center justify-center gap-2 sm:gap-3 border-b border-black/20"
    >
      <span className="animate-blink text-white/90">●</span>
      <span className="uppercase tracking-wider hidden sm:inline">
        Live intake open — Spots limited today.
      </span>
      <span className="uppercase tracking-wider sm:hidden">Live intake open —</span>
      <span
        data-testid={LANDING.urgencyCountdown}
        className="font-mono bg-black/25 px-2 py-0.5 rounded"
      >
        {mm}:{ss}
      </span>
    </div>
  );
}

export function Hero() {
  return (
    <section
      data-testid={LANDING.hero}
      className="relative overflow-hidden pt-16 sm:pt-20 md:pt-28 pb-20 md:pb-28"
    >
      {/* Background image */}
      <div
        className="absolute inset-0 opacity-[0.18]"
        style={{
          backgroundImage: `url(${HERO_BG})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0B]/40 via-[#0A0A0B]/70 to-[#0A0A0B]" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,rgba(240,70,50,0.15),transparent_60%)]" />

      <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Preheadline pill */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="flex justify-center mb-8"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#F04632]/10 border border-[#F04632]/30 text-[#FF7A66] text-xs sm:text-sm font-bold uppercase tracking-[0.18em]">
            <AlertTriangle className="w-4 h-4" strokeWidth={2.5} />
            For deactivated Amazon sellers · Deadline-critical
          </div>
        </motion.div>

        {/* Small kicker */}
        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.05 }}
          className="text-center text-sm sm:text-base text-white/60 uppercase tracking-[0.2em] mb-6 font-semibold"
        >
          Amazon just deactivated your account. Every hour costs you money. Here&apos;s the fix.
        </motion.p>

        {/* Main headline */}
        <motion.h1
          data-testid={LANDING.heroHeadline}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="text-center text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black tracking-tighter leading-[0.95] mb-6"
        >
          Get a <span className="text-[#F04632]">Reinstatement-Ready</span>
          <br className="hidden sm:block" /> Plan of Action in{" "}
          <span className="underline decoration-[#F04632] decoration-8 underline-offset-4">
            minutes
          </span>
          <br className="hidden sm:block" /> — for{" "}
          <span className="text-[#F04632] whitespace-nowrap">$197</span>. Not $5,000.
        </motion.h1>

        {/* Sub-headline */}
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="text-center text-lg sm:text-xl md:text-2xl text-white/70 leading-relaxed max-w-3xl mx-auto mb-10 font-light"
        >
          Skip the $5,000 attorney and the $2,500 reinstatement mill. Answer a few questions,
          connect your Seller Central, and get a{" "}
          <span className="text-white font-semibold">custom, submission-ready Plan of Action</span>{" "}
          engineered by Protego AI — the same 5-part POA architecture Amazon investigators
          actually approve.
        </motion.p>

        {/* VSL Placeholder — temporarily removed (video coming soon) */}

        {/* Primary CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="flex flex-col items-center"
        >
          <CTAButton
            testId={LANDING.heroCta}
            subtext="Secure Stripe checkout · POA delivered to your dashboard in minutes · Refund available before you trigger generation"
          >
            Get My Plan of Action — $197
          </CTAButton>
        </motion.div>

        {/* Stripe Buy Button — official one-click Stripe checkout */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.5 }}
          className="mt-8 flex flex-col items-center"
        >
          <p className="text-xs uppercase tracking-[0.25em] text-white/40 font-bold mb-3">
            — or one-click checkout —
          </p>
          <StripeBuyButton />
        </motion.div>

        {/* Trust badges */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.55 }}
          className="mt-10 flex flex-wrap items-center justify-center gap-x-8 gap-y-4 text-white/50 text-xs sm:text-sm"
        >
          <div className="flex items-center gap-2">
            <Shield className="w-4 h-4 text-[#10B981]" strokeWidth={2.5} />
            <span className="uppercase tracking-widest font-semibold">
              Stripe-Secured Checkout
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Lock className="w-4 h-4 text-[#10B981]" strokeWidth={2.5} />
            <span className="uppercase tracking-widest font-semibold">
              Read-Only Amazon Access
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Timer className="w-4 h-4 text-[#F59E0B]" strokeWidth={2.5} />
            <span className="uppercase tracking-widest font-semibold">Delivered in minutes</span>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
